import { css } from '@emotion/react';
import styled from '@emotion/styled';
import React, { ReactNode } from 'react';
import { breakpoints, colors, spacing } from '../../../../utils/styleguide';
import LanguageDropdown from '../../forms/LanguageDropdown';
import Icon from '../../Icon';
import Typography from '../../text/Typography';

const Wrapper = styled.div<{ hasMobileStaticButton: boolean }>`
  background-color: ${colors.black};
  padding: ${spacing[5]}px ${spacing[4]}px;
  padding-bottom: ${({ hasMobileStaticButton }) =>
    hasMobileStaticButton ? spacing[11] : spacing[5]}px;
  ${breakpoints.tablet} {
    padding: ${spacing[10]}px 90px ${spacing[5]}px;
  }
`;

const MainContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  ${breakpoints.tablet} {
    flex-direction: row;
  }
`;

const LogoWrapper = styled.div`
  display: flex;
  flex-basis: 25%;
  justify-content: center;
  align-items: center;
`;

const Nav = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-top: 20px;
  ${breakpoints.tablet} {
    flex-basis: 75%;
    margin-top: 0;
  }
`;

const NavLink = styled(Typography)`
  transition: color 0.3s ease;
  color: ${colors.white};
  text-decoration: none;
  border-bottom: 1px solid ${colors.white};
  padding-bottom: 4px;
`;

NavLink.defaultProps = {
  variant: 'body',
  tag: 'a',
};

const BottomContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  border-top: 1px solid ${colors.white};
  padding-top: 28px;
  margin-top: 60px;
  ${breakpoints.tablet} {
    flex-direction: row;
  }
`;

const SocialWrapper = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  margin-top: ${spacing[5]}px;
  ${breakpoints.tablet} {
    flex-direction: row;
    margin-top: 0;
  }
`;

const SocialIconsContainer = styled.div`
  display: flex;
  align-items: center;
`;

const SocialIcon = styled(Icon)`
  > * {
    fill: ${colors.white};
  }
  margin-left: ${spacing[3]}px;
  margin-right: ${spacing[3]}px;
  margin-top: ${spacing[2]}px;
  ${breakpoints.tablet} {
    margin-left: ${spacing[5]}px;
    margin-right: 0;
    margin-top: 0;
  }
`;

SocialIcon.defaultProps = {
  size: 24,
};

type Props = {
  navigationLinks?: ReactNode;
  showLanguageSelection?: boolean;
  hasMobileStaticButton?: boolean;
};

const Footer = ({
  showLanguageSelection,
  hasMobileStaticButton = false,
  navigationLinks,
}: Props) => (
  <Wrapper hasMobileStaticButton={hasMobileStaticButton}>
    <MainContentWrapper>
      <LogoWrapper>
        <Icon name="milkywire-full" fill={colors.white} size="80" />
      </LogoWrapper>
      {navigationLinks && <Nav>{navigationLinks}</Nav>}
    </MainContentWrapper>
    <BottomContentWrapper>
      <Typography
        useBuiltInMargins
        color={colors.white}
        css={css`
          margin: 0;
          text-align: center;
          ${breakpoints.tablet} {
            text-align: left;
          }
        `}
      >
        All rights reserved 2021 © Milkywire AB
      </Typography>

      <SocialWrapper>
        <Typography
          useBuiltInMargins
          color={colors.white}
          css={css`
            margin: 0;
          `}
        >
          Follow us
        </Typography>
        <Icon
          name="long-arrow"
          css={css`
            margin-left: ${spacing[1]}px;
            height: 20px;
            width: 40px;
            display: none;
            ${breakpoints.tablet} {
              display: initial;
            }
          `}
        />
        <SocialIconsContainer>
          <a aria-label="instagram" href="https://www.instagram.com/milkywire/">
            <SocialIcon name="instagram" />
          </a>
          <a aria-label="facebook" href="https://www.facebook.com/milkywire/">
            <SocialIcon name="facebook" />
          </a>
          <a aria-label="linkedin" href="https://www.linkedin.com/company/milkywire/about/">
            <SocialIcon name="linkedin" />
          </a>
        </SocialIconsContainer>
      </SocialWrapper>
    </BottomContentWrapper>
    {showLanguageSelection && (
      <LanguageDropdown css={{ marginTop: spacing[4], textAlign: 'center' }} />
    )}
  </Wrapper>
);

export default Footer;
